/**
 * AOK FeatherKnit page interactions.
 *
 * - Scroll-reveal: elements with [data-aok-reveal] fade/translate in when they
 *   enter the viewport.
 * - Count-up: elements with [data-aok-count] animate from 0 to their target
 *   number when they enter the viewport.
 * - Hero video: plays only while in the viewport.
 *
 * Loaded as a module so duplicate <script> tags across sections execute once.
 * Everything no-ops gracefully when JS is unavailable (content stays visible)
 * and respects prefers-reduced-motion.
 */

// Gate class for the reveal CSS. Set here — not inline in the sections — so
// that if this module never loads (blocked/failed JS), the hidden state never
// applies and all content stays visible.
document.documentElement.classList.add('aok-js');

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/* -------------------------------------------------- scroll reveal */

function initReveals() {
  const items = document.querySelectorAll('[data-aok-reveal]:not([data-aok-bound])');
  if (!items.length) return;

  if (reduceMotion.matches || !('IntersectionObserver' in window)) {
    for (const el of items) {
      el.setAttribute('data-aok-bound', '');
      el.classList.add('aok-revealed');
    }
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('aok-revealed');
        observer.unobserve(entry.target);
      }
    },
    { threshold: 0.15, rootMargin: '0px 0px -8% 0px' }
  );

  for (const el of items) {
    el.setAttribute('data-aok-bound', '');
    observer.observe(el);
  }
}

/* -------------------------------------------------- count-up stats */

const COUNT_DURATION = 1400;

/**
 * @param {HTMLElement} el
 */
function runCounter(el) {
  const target = parseFloat(el.getAttribute('data-aok-count') || '0');
  const locale = 'en-IN';
  const finalText = target.toLocaleString(locale);

  // Reserve final width so the layout never shifts while counting.
  el.style.display = 'inline-block';
  el.style.minWidth = `${finalText.length}ch`;

  if (reduceMotion.matches) {
    el.textContent = finalText;
    return;
  }

  const start = performance.now();

  const tick = (/** @type {number} */ now) => {
    const progress = Math.min((now - start) / COUNT_DURATION, 1);
    const eased = 1 - Math.pow(1 - progress, 3);
    el.textContent = Math.round(target * eased).toLocaleString(locale);
    if (progress < 1) requestAnimationFrame(tick);
  };

  requestAnimationFrame(tick);
}

function initCounters() {
  const items = document.querySelectorAll('[data-aok-count]:not([data-aok-bound])');
  if (!items.length) return;

  if (!('IntersectionObserver' in window)) {
    for (const el of items) {
      el.setAttribute('data-aok-bound', '');
      el.textContent = parseFloat(el.getAttribute('data-aok-count') || '0').toLocaleString('en-IN');
    }
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        observer.unobserve(entry.target);
        runCounter(/** @type {HTMLElement} */ (entry.target));
      }
    },
    { threshold: 0.4 }
  );

  for (const el of items) {
    el.setAttribute('data-aok-bound', '');
    observer.observe(el);
  }
}

/* -------------------------------------------------- hero video */

function initHeroVideos() {
  const videos = document.querySelectorAll('.aok-fk-hero video:not([data-aok-bound])');
  if (!videos.length) return;

  for (const video of videos) {
    video.setAttribute('data-aok-bound', '');
    video.muted = true; // belt-and-suspenders for autoplay policies
    video.setAttribute('playsinline', '');

    if (reduceMotion.matches) continue; // poster/first frame only

    if (!('IntersectionObserver' in window)) {
      video.play().catch(() => {});
      continue;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const v = /** @type {HTMLVideoElement} */ (entry.target);
          if (entry.isIntersecting) {
            v.play().catch(() => {});
          } else {
            v.pause();
          }
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(video);
  }
}

/* -------------------------------------------------- boot */

function init() {
  initReveals();
  initCounters();
  initHeroVideos();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

// Re-init when sections are (re)loaded in the theme editor.
document.addEventListener('shopify:section:load', init);
